import { PaymentElement } from "@stripe/react-stripe-js";
import { useState } from "react";
import { useStripe, useElements } from "@stripe/react-stripe-js";
import Button from "../Button/Button";
import "./CheckoutForm.scss";
import axiosClient from "../../axiosClient";
import { ACCESS_TOKEN_KEY, contactEmail, platform } from "../../brand";
import {
  isPasswordValid,
  PASSWORD_VALIDATION_ERROR,
} from "../../utils/passwordRequirements";

async function completeOqSubscription(params: {
  paymentIntentId?: string | null;
  setupIntentId?: string | null;
}) {
  const body: { paymentIntentId?: string; setupIntentId?: string } = {};
  if (params.paymentIntentId) body.paymentIntentId = params.paymentIntentId;
  if (params.setupIntentId) body.setupIntentId = params.setupIntentId;

  const completeResponse = await axiosClient.post(
    "/payments/oq-complete-subscription",
    body,
  );
  const { accessToken } = completeResponse.data;
  if (accessToken) {
    sessionStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
    window.location.assign("/dashboard");
    return;
  }
  // Existing-user path: token was issued at create step (or user was already logged in).
  if (sessionStorage.getItem(ACCESS_TOKEN_KEY)) {
    window.location.assign("/dashboard");
    return;
  }
  window.location.assign(`${process.env.REACT_APP_SITE_URL}/login`);
}

export default function CheckoutForm(props: {
  products: string[],
  isNewUser: boolean,
  couponCode?: string,
}) {
  const stripe = useStripe();
  const elements = useElements();

  const [errorMessage, setErrorMessage] = useState<string | null | undefined>(
    null,
  );
  const [isLoading, setLoading] = useState(false);

  const handleError = (error: any) => {
    setLoading(false);
    setErrorMessage(error.message);
  };

  const handlePayNowButtonClicked = async () => {
    try {
      // email and password fields needed for both sign up paths (new users and existing users)
      const email = (
        document.querySelector('input[name="email"]') as HTMLInputElement
      ).value.trim();
      const password = (
        document.querySelector('input[name="password"]') as HTMLInputElement
      ).value;

      // fields specific for existing user flow
      let name, surname, confirmPassword;

      if (props.isNewUser) {
        name = (
          document.querySelector('input[name="name"]') as HTMLInputElement
        ).value.trim();
        surname = (
          document.querySelector('input[name="surname"]') as HTMLInputElement
        ).value.trim();
        confirmPassword = (
          document.querySelector('input[name="confirmPassword"]') as HTMLInputElement
        ).value;

        // Validation specific for new users
        if (!name || !surname || !confirmPassword) {
          setErrorMessage("Please ensure all fields have been filled.");
          return;
        }

        if (password !== confirmPassword) {
          setErrorMessage('Passwords do not match!')
          return;
        }
      }

      if (!email || !password) {
        setErrorMessage("Please ensure all fields have been filled.");
        return;
      }

      if (!isPasswordValid(password)) {
        setErrorMessage(PASSWORD_VALIDATION_ERROR);
        return;
      }

      if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(email)) {
        setErrorMessage("Invalid email address");
        return;
      }

      if (!stripe || !elements) {
        // Stripe.js has not yet loaded.
        return;
      }

      setLoading(true);

      // Trigger form validation and wallet collection
      const { error: submitError } = await elements.submit();
      if (submitError) {
        handleError(submitError);
        return;
      }

      let response;
      
      if (props.isNewUser) {
        response = await axiosClient.post("/payments/oq-create-subscription", {
          name,
          surname,
          email,
          password,
          products: props.products,
          couponCode: props.couponCode?.trim() || undefined,
          platform,
        });
      }

      else {
        response = await axiosClient.post("/payments/oq-create-subscription-existing-user", {
          email,
          password,
          products: props.products,
          couponCode: props.couponCode?.trim() || undefined,
          platform,
        });
      }

      const { type, clientSecret, paymentIntentId, setupIntentId, accessToken } = response.data;

      // Existing-user create returns a session so complete can require auth.
      // New signup still waits for complete before granting access tokens.
      if (accessToken) {
        sessionStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
      }

      // Do not grant product access until payment is confirmed and
      // /payments/oq-complete-subscription provisions enrolments.
      const confirmParams = {
        elements,
        clientSecret,
        confirmParams: {
          return_url: `${window.origin}/complete-sign-up`,
        },
        redirect: "if_required" as const,
      };

      let confirmedPaymentIntentId: string | null = null;
      let confirmedSetupIntentId: string | null = null;

      if (type === "setup") {
        const { error, setupIntent } = await stripe.confirmSetup(confirmParams);
        if (error) {
          handleError(error);
          return;
        }
        confirmedSetupIntentId = setupIntent?.id || setupIntentId || null;
      } else {
        const { error, paymentIntent } = await stripe.confirmPayment(confirmParams);
        if (error) {
          handleError(error);
          return;
        }
        confirmedPaymentIntentId = paymentIntent?.id || paymentIntentId || null;
      }

      await completeOqSubscription({
        paymentIntentId: confirmedPaymentIntentId,
        setupIntentId: confirmedSetupIntentId,
      });
    } catch (err: any) {
      if (err?.response?.status == 400 || err?.response?.status == 402) {
        handleError({
          message: err.response?.data?.error
        });
      }
      else {
        console.log(err);
        if (!(document.getElementById("agree_to_terms") as HTMLInputElement)?.checked) {
          handleError({
            message: "Please make sure to agree to terms and conditions!"
          });
          return;
        }
        handleError({
          message: `Payment failed. Please contact ${contactEmail} for support`
        });
      }
      return;
    }
  };

  return (
    <div id="CheckoutForm">
      <PaymentElement
        options={{
          paymentMethodOrder: ["card"],
        }}
      />
      <Button
        text={isLoading ? "Processing ... " : "Pay now"}
        disabled={isLoading || !stripe || !elements}
        onClick={handlePayNowButtonClicked}
      ></Button>
      {/* Show any error or success messages */}
      {errorMessage && <div id="payment-error-message">{errorMessage}</div>}
    </div>
  );
}
